import classNames from "classnames";
import {PropsWithChildren, useContext, useState} from "react";
import {FiEye} from "react-icons/fi";

import Button from "@/components/button/Button.tsx";
import {UserContext} from "@/components/context/UserContextProvider.tsx";
import {StatusPill} from "@/components/statusPill/StatusPill.tsx";
import {PillStatus} from "@/components/statusPill/StatusPill.types.ts";
import {EmptyTable} from "@/components/table/subComponents/EmptyTable/EmptyTable.tsx";
import {Table} from "@/components/table/Table.tsx";
import {ColumnSpec} from "@/components/table/Table.types.ts";
import {durationSecFn, roundDurationSecs, shortDateTimeUser} from "@/lib/format.tsx";
import {CheckDetailsAlert} from "@/lib/queries/checks.ts";
import {PropsWithClassName} from "@/lib/types/props.ts";
import {AlertDetailsModal} from "@/pages/alerting/components/details/AlertDetailsModal.tsx";

interface AlertActionProps extends PropsWithChildren, PropsWithClassName {
  alert: CheckDetailsAlert;
}

const AlertAction = ({alert, children, className}: AlertActionProps) => {
  const [showAlertDetails, setShowAlertDetails] = useState<boolean>(false);

  return (
    <>
      <Button variant="ghost" onClick={() => setShowAlertDetails(true)} className={className}>
        {children}
      </Button>
      <AlertDetailsModal
        id={alert.id}
        show={showAlertDetails}
        hide={() => {
          setShowAlertDetails(false);
        }}
      />
    </>
  );
};

export interface Top5AlertsTableProps {
  checkId: number;
  flattenAlerts: CheckDetailsAlert[];
  flattenAlertsTotal: number;
}

export const Top5AlertsTable = ({flattenAlerts}: Top5AlertsTableProps) => {
  const {user} = useContext(UserContext);

  const specs: ColumnSpec<CheckDetailsAlert>[] = [
    {
      fieldName: "time",
      fieldTitle: "Alert Time",
      accessor: (alert) => <div className="text-nowrap">{shortDateTimeUser(alert.created_at, user!)}</div>,
    },
    {
      fieldName: "reason",
      fieldTitle: "Reason",
      accessor: (alert) => (
        <AlertAction
          alert={alert}
          className={classNames("ps-0", {
            "text-success": alert.state_is_up,
            "text-danger": !alert.state_is_up,
          })}
        >
          {alert.output}
        </AlertAction>
      ),
    },
    {
      fieldName: "duration",
      fieldTitle: "Duration",
      accessor: (alert) => <div className="text-nowrap">{durationSecFn(roundDurationSecs(alert.duration))}</div>,
    },
    {
      fieldName: "ignored",
      fieldTitle: "",
      accessor: (alert) => (
        <>{alert.ignored && <StatusPill status={PillStatus.IGNORED} type="checks" variant="textAndIcon" />}</>
      ),
    },
    {
      fieldName: "details",
      accessor: (alert) => (
        <AlertAction alert={alert} className={"p-2"}>
          <FiEye />
        </AlertAction>
      ),
    },
  ];

  return (
    <Table rows={flattenAlerts} specs={specs} noDataComponent={<EmptyTable>No Alerts in this period.</EmptyTable>} />
  );
};
